import { get } from 'lodash-es';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorBody, getApiClientErrorStatus } from '@/services/api/errors';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';

/**
 * The statuses the Ordway AI-credits purchase workflow reports. The purchase POST resolves with
 * `pending` or `success`; the status poll adds the terminal `declined` and `failed`. The generated
 * spec types both as a bare `string`, so callers narrow through
 * {@link parseOrdwayAiCreditsPurchaseStatus} rather than casting.
 */
const ORDWAY_AI_CREDITS_PURCHASE_STATUSES = ['pending', 'success', 'declined', 'failed'] as const;

export type OrdwayAiCreditsPurchaseStatus = (typeof ORDWAY_AI_CREDITS_PURCHASE_STATUSES)[number];

/** The known status matching `status`, or `undefined` when the server reported something else. */
function parseOrdwayAiCreditsPurchaseStatus(status: string | undefined): OrdwayAiCreditsPurchaseStatus | undefined {
  return ORDWAY_AI_CREDITS_PURCHASE_STATUSES.find((known) => known === status);
}

/**
 * What a failed purchase establishes about the card.
 *
 * - `declined` — Ordway refused the payment, so nothing was collected and buying again is the fix.
 * - `failed` — the workflow ended in a state the status endpoint cannot tell apart from a charge. It
 *   collapses every non-declined terminal error to one token, and two of them happen *after* a
 *   successful collection: a payment that collected without applying to the invoice, and a grant that
 *   failed once the card was already charged.
 * - `indeterminate` — the poll never reached a verdict, so the charge may still be in flight.
 *
 * Only `declined` leaves buying open. The rest may have taken the customer's money, and the server's
 * duplicate guard lapses once the first workflow ends, so a second attempt would charge again.
 */
export type OrdwayAiCreditsPurchaseFailure = 'declined' | 'failed' | 'indeterminate';

/** A purchase failure carrying what it establishes about the charge, alongside its user-facing copy. */
export class OrdwayAiCreditsPurchaseError extends Error {
  readonly failure: OrdwayAiCreditsPurchaseFailure;

  constructor(message: string, failure: OrdwayAiCreditsPurchaseFailure) {
    super(message);
    this.name = 'OrdwayAiCreditsPurchaseError';
    this.failure = failure;
  }
}

/** What `error` establishes about the charge, or `undefined` when it is not a purchase failure. */
export function ordwayAiCreditsPurchaseFailure(error: unknown): OrdwayAiCreditsPurchaseFailure | undefined {
  return error instanceof OrdwayAiCreditsPurchaseError ? error.failure : undefined;
}

/** Mirrors the copy the purchase endpoint returns when the card on file is declined. */
export const ORDWAY_AI_CREDITS_DECLINED_MESSAGE = 'Your card was declined. Update your payment method and try again.';

/** Copy for a terminal failure whose relationship to the charge the server could not establish. */
export const ORDWAY_AI_CREDITS_FAILURE_MESSAGE = "Your purchase couldn't be processed. Contact support.";

/**
 * The error a terminal `failed` establishes, wherever it is read from — the workflow's status poll or
 * a synchronous response carrying the same token. Both mean the purchase ended in a state that cannot
 * be told apart from one that collected, so both must classify as `failed` and lock buying.
 */
export function ordwayAiCreditsFailedError(): OrdwayAiCreditsPurchaseError {
  return new OrdwayAiCreditsPurchaseError(ORDWAY_AI_CREDITS_FAILURE_MESSAGE, 'failed');
}

/**
 * Shown when the purchase is still running once polling gives up. Polling stops well before the
 * workflow's own deadline, and the workflow orders, bills, and waits for an invoice before charging
 * the card, so at this point the charge may not have been attempted — and may still succeed. The copy
 * therefore asserts neither a payment nor a failure, and points at the balance rather than inviting a
 * retry that could double-charge.
 */
export const ORDWAY_AI_CREDITS_STILL_PROCESSING_MESSAGE =
  'Your purchase is still processing. Check your credit balance in a few minutes.';

/**
 * The synchronous outcome a services build predating the workflow reports, or `undefined` when the
 * response carries no token this client trusts. Only explicit tokens count: the flow this replaces
 * treated every non-`failed` value as a purchase, which is what let a `pending` response read as a
 * completed sale. An unrecognized value stays indeterminate instead.
 */
export function parseLegacyOrdwayPurchaseOutcome(response: unknown): 'success' | 'failed' | undefined {
  const status = get(response, 'status');
  if (status === 'success' || status === 'granted') {
    return 'success';
  }
  return status === 'failed' ? 'failed' : undefined;
}

/** The in-flight purchase a 409 from the purchase endpoint points at. */
export type OrdwayAiCreditsPurchaseConflict = {
  /**
   * The Temporal run id of the purchase already running for this account, when the server supplied
   * one. Absent when it could not name the run — the conflict is still real and nothing was charged
   * for this attempt, there is just nothing to poll.
   */
  runId?: string;
  /** Its quantity — present only when the server could read it from the running workflow. */
  quantity?: number;
};

/**
 * The in-flight purchase described by a 409 from the Ordway AI-credits purchase endpoint, or
 * `undefined` when the error is not a conflict at all. The `meta` is read defensively: the status
 * alone establishes that another purchase is running and that this attempt was refused, so a missing
 * or blank run id narrows what the caller can do rather than making it an ordinary failure.
 */
export function parseOrdwayAiCreditsPurchaseConflict(error: unknown): OrdwayAiCreditsPurchaseConflict | undefined {
  if (getApiClientErrorStatus(error) !== 409) {
    return undefined;
  }

  const meta = get(getApiClientErrorBody(error), 'errors[0].meta');
  const runId = get(meta, 'run_id');
  const quantity = get(meta, 'quantity');
  return {
    runId: typeof runId === 'string' && !isEmptyString(runId) ? runId : undefined,
    quantity: typeof quantity === 'number' ? quantity : undefined,
  };
}

/** One status-poll attempt's outcome. `unavailable` is a transient request failure worth retrying. */
export type OrdwayAiCreditsPurchaseStatusRead =
  | { kind: 'status'; status: OrdwayAiCreditsPurchaseStatus }
  | { kind: 'unrecognized'; status: string }
  | { kind: 'not-found' }
  | { kind: 'unavailable' };

/** Reads the purchase workflow's current status for one poll attempt. */
export async function readOrdwayAiCreditsPurchaseStatus(
  accountId: string,
  runId: string,
): Promise<OrdwayAiCreditsPurchaseStatusRead> {
  try {
    const res = await hydraUiApi.billing.getAiCreditsOrdwayPurchaseStatus({
      params: { path: { account_id: accountId }, query: { run_id: runId } },
    });
    const status = parseOrdwayAiCreditsPurchaseStatus(res.status);
    return status != null ? { kind: 'status', status } : { kind: 'unrecognized', status: res.status };
  } catch (error) {
    return getApiClientErrorStatus(error) === 404 ? { kind: 'not-found' } : { kind: 'unavailable' };
  }
}

/**
 * The user-facing error for a terminal status, or `undefined` while the purchase is still pending.
 * `success` is handled by the caller, which resolves rather than throwing.
 */
export function ordwayAiCreditsPurchaseStatusError(
  status: Exclude<OrdwayAiCreditsPurchaseStatus, 'success'>,
): Error | undefined {
  switch (status) {
    case 'pending':
      return undefined;
    case 'declined':
      return new OrdwayAiCreditsPurchaseError(ORDWAY_AI_CREDITS_DECLINED_MESSAGE, 'declined');
    case 'failed':
      return ordwayAiCreditsFailedError();
    default: {
      const exhaustive: never = status;
      return new Error(String(exhaustive));
    }
  }
}

/**
 * Reports an indeterminate poll outcome to Sentry and returns the error to throw. A run the status
 * endpoint cannot describe, or a status this client does not recognize, says nothing about whether
 * the card was charged — so this reports the same unknown outcome as a timeout rather than the
 * failure copy, which would be a definite claim the poll never established.
 */
export function reportIndeterminateOrdwayAiCreditsPurchase(diagnostic: string): OrdwayAiCreditsPurchaseError {
  sentryCaptureException(new Error(diagnostic));
  return ordwayAiCreditsIndeterminateError();
}

/** The error for an outcome the client never established, paired with the copy that claims nothing. */
export function ordwayAiCreditsIndeterminateError(): OrdwayAiCreditsPurchaseError {
  return new OrdwayAiCreditsPurchaseError(ORDWAY_AI_CREDITS_STILL_PROCESSING_MESSAGE, 'indeterminate');
}

/**
 * Whether a purchase POST that threw leaves open that the server started the purchase anyway.
 *
 * The endpoint refuses a bad request — not an Ordway account, no saved card, a non-card default —
 * before it creates an order or starts a workflow, so those establish that nothing was charged. A
 * lost response is the opposite: a client timeout or a network failure says nothing about what the
 * server did, and a 5xx can arrive *after* the work began — on the synchronous path a grant failing
 * once the card was charged is exactly a 500. Those must keep buying locked, because the server's
 * duplicate guard lapses once that first purchase ends.
 *
 * A 5xx raised before anything was created (a slow Ordway payment-method lookup answering 503) locks
 * too, which is stricter than it needs to be. That is the deliberate side to err on: an unnecessary
 * lock costs a reopened modal, and the customer can still see their balance, while a missed one
 * costs a second charge.
 */
export function ordwayAiCreditsPurchaseMayHaveStarted(error: unknown): boolean {
  const status = getApiClientErrorStatus(error);
  return status === undefined || status >= 500;
}

/** Running state a purchase poll carries across its attempts. */
export type OrdwayAiCreditsPollTracker = {
  /** How many not-found reads have arrived back-to-back. Resets on any read the route answered. */
  readonly notFoundStreak: number;
  /** Folds one attempt's read into the running state. */
  record: (read: OrdwayAiCreditsPurchaseStatusRead) => void;
  /** Diagnostic for a poll that used up `attempts` without reaching a terminal status. */
  timedOutDiagnostic: (attempts: number) => string;
};

/**
 * Bookkeeping a poll keeps across attempts, held here so the loop itself stays a read-and-decide.
 * It owns two rules that only make sense over a whole poll rather than a single read: the
 * consecutive-404 streak, and reporting an unrecognized status token no more than once.
 */
export function createOrdwayAiCreditsPollTracker(runId: string): OrdwayAiCreditsPollTracker {
  let notFound = 0;
  let lastStatus: OrdwayAiCreditsPurchaseStatus | undefined;
  let reportedUnrecognized = false;

  return {
    get notFoundStreak() {
      return notFound;
    },

    record(read) {
      if (read.kind === 'not-found') {
        notFound += 1;
        return;
      }
      // An `unavailable` read is the request failing, not the route answering, so it neither
      // extends the 404 streak nor proves the route is live — leave the streak untouched.
      if (read.kind === 'unavailable') {
        return;
      }
      // A read the route answered proves it is live, so the tolerance counts consecutive 404s. During
      // a rolling deploy reads alternate between instances, and cumulative counting would exhaust the
      // budget on a healthy run.
      notFound = 0;
      if (read.kind === 'status') {
        lastStatus = read.status;
        return;
      }
      if (!reportedUnrecognized) {
        // Once per poll: the token repeats on every attempt, and the run may still settle normally.
        reportedUnrecognized = true;
        sentryCaptureException(
          new Error(`Ordway AI credits purchase run ${runId} reported an unrecognized status: ${read.status}`),
        );
      }
    },

    timedOutDiagnostic(attempts) {
      return `Ordway AI credits purchase polling timed out after ${attempts} attempts for run ${runId} (lastStatus: ${lastStatus ?? 'none'})`;
    },
  };
}
